import { useEffect, useState } from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import { doc, setDoc, getDoc, collection, onSnapshot } from 'firebase/firestore';
import { auth, db } from '../firebase';
import { useAuthStore, type Subscription } from '../store/authStore';
import { createAppUser, createBasicSubscription } from '../lib/authSession';
import { downgradeExpiredSubscription } from '../lib/adminFirestore';
import { getExamSessions } from '../lib/examStats';

function buildSubscriptionFromFirestoreSub(fireSub: any): Subscription {
  const endDate = fireSub.end_date ? new Date(fireSub.end_date) : new Date();
  const now = new Date();
  const isExpired = endDate < now;
  const daysRemaining = Math.max(0, Math.ceil((endDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)));
  const planName = fireSub.plan_name || 'Basic';
  return {
    id: fireSub.plan_id || 2,
    status: fireSub.status || 'active',
    end_date: fireSub.end_date || createBasicSubscription().end_date,
    plan: {
      name: planName,
      name_ar: planName === 'Trial' ? 'تجريبي' : planName === 'Basic' ? 'أساسي' : planName === 'Pro' ? 'احترافي' : 'مؤسسي',
      question_limit: planName === 'Basic' ? 20 : null,
      has_full_access: planName === 'Basic' ? 0 : 1,
      has_analytics: planName === 'Basic' ? 0 : 1,
      has_priority_support: planName === 'Enterprise' ? 1 : 0,
    },
    daysRemaining: planName === 'Basic' ? null : daysRemaining,
    isExpired,
  };
}

export function useAuth() {
  const { user, setUser, setSubscription, setIsAdmin } = useAuthStore();
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let subscriptionUnsubscribe: (() => void) | null = null;

    const unsubscribeAuth = onAuthStateChanged(auth, async (firebaseUser) => {
      if (!firebaseUser) {
        localStorage.removeItem('token');
        setUser(null);
        setSubscription(null);
        setIsAdmin(false);
        setIsLoading(false);
        if (subscriptionUnsubscribe) {
          subscriptionUnsubscribe();
          subscriptionUnsubscribe = null;
        }
        return;
      }

      const token = await firebaseUser.getIdToken();
      const appUser = createAppUser(firebaseUser);

      localStorage.setItem('token', token);
      setUser(appUser);
      const adminEmails = ['ashrafede@gmail.com'];
      const adminDisplayNames = ['Ashraf EL desoky'];
      setIsAdmin(
        appUser.email.includes('admin') ||
        adminEmails.includes(appUser.email) ||
        adminDisplayNames.includes(appUser.display_name || '')
      );

      // Set up real-time subscription listener
      const subsCollection = collection(db, 'users', appUser.firebase_uid, 'subscriptions');
      subscriptionUnsubscribe = onSnapshot(subsCollection, async (snap: any) => {
        let subscription: Subscription;
        let docs = snap.docs.map((d: any) => ({ id: d.id, ...d.data() }));
        docs.sort((a: any, b: any) => new Date(b.created_at || b.start_date).getTime() - new Date(a.created_at || a.start_date).getTime());
        if (docs.length === 0) {
          // No subscription yet: create Basic and seed it
          subscription = createBasicSubscription();
          try {
            const subRef = doc(subsCollection);
            await setDoc(subRef, {
              plan_id: subscription.id,
              plan_name: subscription.plan.name,
              status: subscription.status,
              start_date: new Date().toISOString(),
              end_date: subscription.end_date,
              created_at: new Date().toISOString(),
            });
          } catch (err) {
            console.error('Failed to seed Basic subscription:', err);
          }
        } else {
          const fireSub = docs[0];
          const built = buildSubscriptionFromFirestoreSub(fireSub);
          if (built.isExpired && built.plan.name !== 'Basic') {
            try {
              await downgradeExpiredSubscription(appUser.firebase_uid);
              subscription = createBasicSubscription();
            } catch (err) {
              console.error('Failed to downgrade expired subscription:', err);
              subscription = built;
            }
          } else {
            subscription = built;
          }
        }
        setSubscription(subscription);
        console.log('[useAuth] subscription updated:', subscription.plan.name, subscription.end_date, subscription.isExpired ? 'expired' : 'active');

        // Sync user summary to Firestore
        try {
          const userRef = doc(db, 'users', appUser.firebase_uid);
          const existingSnap = await getDoc(userRef);
          const now = new Date().toISOString();
          await setDoc(userRef, {
            uid: appUser.firebase_uid,
            id: appUser.id,
            email: appUser.email,
            display_name: appUser.display_name || null,
            photo_url: appUser.photo_url || null,
            preferred_language: appUser.preferred_language,
            plan_name: subscription.plan.name,
            status: subscription.status,
            last_login: now,
            created_at: existingSnap.exists() ? (existingSnap.data().created_at || now) : now,
          }, { merge: true });
        } catch (err) {
          console.error('Firestore user sync error:', err);
        }
      }, (err: any) => {
        console.error('Subscription snapshot error:', err);
      });

      // Sync user to Firestore (Firebase tables) for admin dashboard and ensure clean session data
      try {
        const userRef = doc(db, 'users', appUser.firebase_uid);
        const existingSnap = await getDoc(userRef);
        const now = new Date().toISOString();
        await setDoc(userRef, {
          uid: appUser.firebase_uid,
          id: appUser.id,
          email: appUser.email,
          display_name: appUser.display_name || null,
          photo_url: appUser.photo_url || null,
          preferred_language: appUser.preferred_language,
          plan_name: 'Basic',
          status: 'active',
          last_login: now,
          created_at: existingSnap.exists() ? (existingSnap.data().created_at || now) : now,
        }, { merge: true });
      } catch (err) {
        console.error('Firestore user sync error:', err);
      }

      // Register user in the admin users registry + ensure clean session data
      try {
        const USERS_REGISTRY_KEY = 'pmp_admin_users_registry';
        const existing: any[] = JSON.parse(localStorage.getItem(USERS_REGISTRY_KEY) || '[]');
        const sessionKey = `pmp_exam_stats_v1_uid_${appUser.firebase_uid}`;
        const firstSeenKey = `pmp_first_seen_uid_${appUser.firebase_uid}`;

        const isKnownUser = !!existing.find((u: any) => u.id === appUser.id);
        const hasBeenSeen = !!localStorage.getItem(firstSeenKey);

        if (!hasBeenSeen) {
          localStorage.setItem(firstSeenKey, new Date().toISOString());
          localStorage.setItem(sessionKey, '[]');
        }

        if (!isKnownUser) {
          const updated = [
            {
              id: appUser.id,
              email: appUser.email,
              display_name: appUser.display_name,
              firebase_uid: appUser.firebase_uid,
              preferred_language: appUser.preferred_language,
              registered_at: new Date().toISOString(),
              plan_name: 'Basic',
              status: 'active',
            },
            ...existing,
          ];
          localStorage.setItem(USERS_REGISTRY_KEY, JSON.stringify(updated));
        }
      } catch { /* ignore */ }

      // Migrate anonymous sessions to the logged-in user
      try {
        const anonymousSessions = getExamSessions(null);
        if (anonymousSessions.length > 0) {
          const userSessions = getExamSessions(appUser.firebase_uid);
          const merged = [...anonymousSessions, ...userSessions].slice(0, 100);
          localStorage.setItem(`pmp_exam_stats_v1_uid_${appUser.firebase_uid}`, JSON.stringify(merged));
          localStorage.setItem('pmp_exam_stats_v1_anonymous', '[]');
        }
      } catch (err) {
        console.error('Migrate anonymous sessions error:', err);
      }

      setIsLoading(false);
    });

    return () => {
      unsubscribeAuth();
      if (subscriptionUnsubscribe) subscriptionUnsubscribe();
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return { user, isLoading };
}
